import {React, useEffect} from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Settings, 
  Users, 
  Target, 
  Eye, 
  ChevronRight, 
  Award, 
  Clock,
  BadgeCheck,
  Weight,
  History,
} from 'lucide-react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Image1 from '../Assets/Home/Home_1.jpg';
import Image2 from '../Assets/Home/Home_2.avif';

const AboutPage = () => {
  // Animation Variants
  useEffect(() => {
  document.title = "Marvel Sato | About Us";
}, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const fadeInRight = {
    initial: { opacity: 0, x: -50 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const fadeInLeft = {
    initial: { opacity: 0, x: 50 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <Header />

      {/* --- HERO SECTION --- */}
      <section className="relative h-[50vh] flex items-center justify-center bg-slate-900 pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={Image1} 
            className="w-full h-full object-cover opacity-30" 
            alt="About Background" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white"></div>
        </div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-slate-900 tracking-tighter italic">
            ABOUT <span className="text-orange-500 not-italic">MARVEL </span>
            <span className="text-orange-500 not-italic">SATO</span> 
          </h1>
          <div className="h-1.5 w-24 bg-orange-500 mx-auto mt-4 rounded-full"></div>
        </motion.div>
      </section>

      {/* --- SECTION 1: ABOUT US --- */}
      <section id="about-content" className="py-20 px-6 md:px-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeInRight}>
            <h4 className="text-orange-500 font-bold uppercase tracking-widest mb-4">Our Heritage</h4>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6 uppercase leading-tight">
              Two Decades of Engineering <br className="hidden md:block" /> Excellence & Innovation
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6 text-lg">
              Marvel Enterprises has established itself as a cornerstone in the industrial machinery sector. From our humble beginnings to becoming a leader in truck-mounted cranes and hydraulic systems, our focus has always been on precision engineering.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              We specialize in creating robust solutions for complex lifting challenges. Our team of certified engineers works tirelessly to ensure that every piece of equipment bearing the Marvel name meets international safety and quality standards.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <Award className="text-orange-500" size={32} />
                <span className="font-bold text-slate-800">ISO Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="text-orange-500" size={32} />
                <span className="font-bold text-slate-800">20+ Years Exp</span>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeInLeft} className="relative mt-10 lg:mt-0">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-orange-500 rounded-2xl z-0"></div>
            <img 
              src={Image2}
              className="relative z-10 rounded-2xl shadow-2xl grayscale hover:grayscale-0 transition-all duration-700 w-full object-cover" 
              alt="Workshop" 
            />
          </motion.div>
        </div>
      </section>

      {/* --- SECTION 2: WHY CHOOSE US --- */}
      <section className="bg-slate-50 py-24 px-6 md:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase">Why Choose Marvel?</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">We don't just sell machines; we provide the safety and reliability your business depends on every single day.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: <ShieldCheck size={40} />, 
                title: "Safety First", 
                desc: "Every crane and lift undergoes rigorous load-testing and safety audits before delivery." 
              },
              { 
                icon: <Settings size={40} />, 
                title: "Customized Engineering", 
                desc: "Tailor-made solutions designed specifically for your unique industrial requirements." 
              },
              { 
                icon: <Users size={40} />, 
                title: "Expert Support", 
                desc: "Dedicated maintenance teams available 24/7 to ensure zero downtime for your operations." 
              },
                            { 
                icon: <BadgeCheck size={40} />, 
                title: "Quality Products", 
                desc: "Precision engineering and rigorous testing to guarantee world-class safety and performance standards." 
              },
                            { 
                icon: <Weight size={40} />, 
                title: "Heavy Duty", 
                desc: "High-tensile construction built to dominate the most demanding industrial environments."
              },
                            { 
                icon: <History size={40} />, 
                title: "Experience", 
                desc: "Decades of manufacturing expertise delivering sophisticated technical lifting solutions." 
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.2 }}
                className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-orange-500 mb-6">{item.icon}</div>
                <h3 className="text-xl font-black text-slate-900 mb-4 uppercase">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     {/* --- SECTION 3: MISSION & VISION --- */}
      <section className="py-12 md:py-24 px-6 md:px-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Mission */}
          <motion.div 
            {...fadeInRight}
            className="group relative overflow-hidden bg-slate-900 rounded-[1.5rem] md:rounded-[2.5rem] p-6 md:p-12 text-white"
          >
            {/* Background Icon: Smaller and further in the corner on mobile */}
            <div className="absolute -top-6 -right-6 p-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
              <Target className="w-32 h-32 md:w-64 md:h-64" />
            </div>
            
            <div className="relative z-10">
              {/* Smaller icon box for mobile */}
              <div className="bg-orange-500 w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-8">
                <Target className="w-5 h-5 md:w-8 md:h-8" />
              </div>
              <h2 className="text-xl md:text-3xl font-black uppercase italic mb-3 md:mb-6">Our Mission</h2>
              <p className="text-slate-400 text-sm md:text-lg leading-relaxed mb-6 md:mb-8">
                To empower global industries by manufacturing the safest, most efficient, and technologically advanced lifting solutions.
              </p>
              <ul className="space-y-2 md:space-y-4">
                {['Innovation in Design', 'Zero Accident Policy', 'Customer-Centric Growth'].map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-[10px] md:text-sm font-bold uppercase tracking-widest">
                    <ChevronRight className="text-orange-500 flex-shrink-0" size={14} /> {point}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Vision */}
          <motion.div 
            {...fadeInLeft}
            className="group relative overflow-hidden bg-orange-500 rounded-[1.5rem] md:rounded-[2.5rem] p-6 md:p-12 text-white"
          >
            <div className="absolute -top-6 -right-6 p-4 opacity-20 group-hover:scale-110 transition-transform duration-500">
              <Eye className="w-32 h-32 md:w-64 md:h-64" />
            </div>

            <div className="relative z-10">
              <div className="bg-slate-900 w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-8">
                <Eye className="w-5 h-5 md:w-8 md:h-8" />
              </div>
              <h2 className="text-xl md:text-3xl font-black uppercase italic mb-3 md:mb-6">Our Vision</h2>
              <p className="text-orange-950 text-sm md:text-lg leading-relaxed mb-6 md:mb-8 font-medium">
                We at MARVEL SATO PVT LTD are driven by a strong commitment to rethink lifting solutions and help our global partners succeed.
              </p>
              <div className="bg-white/20 backdrop-blur-md p-4 md:p-6 rounded-xl md:rounded-2xl">
                <p className="text-[10px] md:text-sm font-bold italic">"Lifting the future with precision."</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;